import { isoAndUnixZodSchema } from "@@/shared/date-time/iso-and-unix";
import type { DurationInMinutes } from "@@/shared/date-time/time-spans";
import { userIdZodSchema } from "@@/shared/users/user-id";
import type { Except } from "type-fest";
import { z } from "zod";
import { signatureFactory } from "../signature-factory";
import type {
    _OrderEventBaseFactoryParams} from "./_order-base.event";
import {
    _orderEventBaseFactory,
    _orderEventBaseZodObject,
} from "./_order-base.event";

const orderEventConfirmedZodSchema = _orderEventBaseZodObject.merge(
    z.object({
        type: z.literal("CONFIRMED"),
        prepTime: z
            .number()
            .min(10) as unknown as z.ZodSchema<DurationInMinutes>,
        confirmedBy: userIdZodSchema.optional(),
        estimatedDeliveryTime: isoAndUnixZodSchema.optional(),
    }),
);
type OrderConfirmedEvent = z.infer<typeof orderEventConfirmedZodSchema>;

// Factory params
type FactoryParams = _OrderEventBaseFactoryParams &
    Except<
        OrderConfirmedEvent,
        "type" | "_id" | "_type" | "_createdAt" | "signature"
    >;

type Params = Except<FactoryParams, "signature">;

const orderEventConfirmedFactory = (params: Params): OrderConfirmedEvent => ({
    ..._orderEventBaseFactory({
        orderId: params.orderId,
        orderGroupId: params.orderGroupId,
        _version: params._version,
        providerId: params.providerId,
        providerSlug: params.providerSlug,
        signature: signatureFactory(params.orderId, "CONFIRMED"),
    }),

    type: "CONFIRMED",
    prepTime: params.prepTime,
    confirmedBy: params.confirmedBy,
    estimatedDeliveryTime: params.estimatedDeliveryTime,
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isOrderConfirmedEvent = (event: any): event is OrderConfirmedEvent => {
    return (
        !!event && event._type === "ORDER_EVENT" && event?.type === "CONFIRMED"
    );
};

export {
    isOrderConfirmedEvent,
    orderEventConfirmedFactory,
    orderEventConfirmedZodSchema,
};
export type { OrderConfirmedEvent };
